import { useTranslation } from "react-i18next";
import style from "./infoSlab.module.css";
import { createStyles, Progress, Box, Text, Group, SimpleGrid, rem, Badge, Flex, Stack } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { calculateAndCategorizeAIScore } from "../../hooks/calculateAndCategorizeAIScore";

const useStyles = createStyles((theme) => ({
    progressLabel: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1,
        fontSize: "16px",
        fontWeight: "500",
    },
    progressLabelSmall: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1,
        fontSize: "13px",
        fontWeight: "500",
    },

    stat: {
        borderBottom: `${rem(3)} solid`,
        paddingBottom: rem(5),
    },

    statCount: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1.3,
    },

    diff: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        display: "flex",
        alignItems: "center",
    },

    icon: {
        color: theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.gray[4],
    },
}));

const InfoSlab = ({ scan, aiScore, displayExplainations = true, displayBlocks = true, smallVersion = false, segmentIndex = null, blocks: blocksOverride = null }) => {
    const { t } = useTranslation(["common"]);

    const { classes } = useStyles();

    const { aiScore: rawCalculatedAIScore, category: calculatedAiScoreCategoryName } = calculateAndCategorizeAIScore(aiScore, scan?.version, scan?.aiDetectionStrictMode);
    const calculatedAIScore = Math.min(99.5, Math.max(0.5, rawCalculatedAIScore));

    // Strict-mode segments have no detector blocks, so the caller can pass sentences derived client-side.
    const blocks = blocksOverride ?? (segmentIndex !== null ? scan?.paragraphs?.[segmentIndex]?.blocks : null);

    const blocksScrollRef = useRef(null);
    const [blocksOverflow, setBlocksOverflow] = useState({ top: false, bottom: false });

    const updateBlocksOverflow = useCallback(() => {
        const element = blocksScrollRef.current;
        if (!element) return;

        const distanceToBottom = element.scrollHeight - element.clientHeight - element.scrollTop;

        setBlocksOverflow((current) => {
            const top = element.scrollTop > 1;
            const bottom = distanceToBottom > 1;
            if (current.top === top && current.bottom === bottom) return current;
            return { top, bottom };
        });
    }, []);

    // The fades depend on measured layout, so they are recalculated whenever the sentence list or the
    // available height changes rather than only on scroll.
    useEffect(() => {
        const element = blocksScrollRef.current;
        if (!element) return;

        updateBlocksOverflow();

        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(updateBlocksOverflow);
        observer.observe(element);
        return () => observer.disconnect();
    }, [blocks, updateBlocksOverflow]);

    const segments = [
        {
            value: 100 - calculatedAIScore,
            color: "var(--green)",
            label: 100 - calculatedAIScore > 20 ? `${Math.ceil((100 - calculatedAIScore) * 10) / 10}%` : undefined,
        },
        {
            value: calculatedAIScore,
            color: "var(--red)",
            label: calculatedAIScore > 20 ? `${Math.floor(calculatedAIScore * 10) / 10}%` : undefined,
        },
    ];

    const getBorderColor = () => {
        if (calculatedAiScoreCategoryName === "AiGenerated" || calculatedAiScoreCategoryName === "ProbablyAiGenerated") return smallVersion ? style.borderRedSmall : style.borderRed;
        if (calculatedAiScoreCategoryName === "Unsure") return smallVersion ? style.borderYellowSmall : style.borderYellow;
        return smallVersion ? style.borderGreenSmall : style.borderGreen;
    };

    const titleMap = {
        AiGenerated: t("scan.result.title.ai"),
        ProbablyAiGenerated: t("scan.result.title.ai"),
        Unsure: t("scan.result.title.unsure"),
        ProbablyOriginal: t("scan.result.title.probably-original"),
        Original: t("scan.result.title.original"),
    };

    return (
        <div className={`${smallVersion ? style.commentSmall : style.comment} ${getBorderColor()}`}>
            <Group position="apart" mb={smallVersion ? "0.5rem" : "0rem"}>
                <Flex align="center" spacing="xs" justify={"space-between"} w={"100%"} wrap={"wrap"}>
                    {scan?.aiDetectionStrictMode ? (
                        <Text fz={smallVersion ? "md" : "lg"} fw={700}>
                            {titleMap[calculatedAiScoreCategoryName]}
                        </Text>
                    ) : (
                        <Text fz={smallVersion ? "md" : "lg"} fw={700}>
                            {titleMap[calculatedAiScoreCategoryName]}
                        </Text>
                    )}

                    {scan?.aiDetectionStrictMode && (
                        <Badge radius={"xs"} color="indigo" mb={"0.25rem"} mt={"0.5rem"}>
                            {t("scan.result.description.strict-mode")}
                        </Badge>
                    )}
                </Flex>
            </Group>

            {displayExplainations && (
                <div>
                    <Text fz="sm">{`${t("scan.result.description.intro")} ${calculatedAIScore}% ${t("scan.result.description.explaination")}`}</Text>

                    {/* {calculatedAIScore > (scan?.aiDetectionStrictMode ? 0.8 : 0.9) && (
                        <>
                            <br />
                            <Text fz="sm">{t("scan.result.description.ai")}</Text>
                        </>
                    )} */}
                </div>
            )}

            <Progress
                sections={segments}
                size={smallVersion ? 24 : 34}
                classNames={{ label: smallVersion ? classes.progressLabelSmall : classes.progressLabel }}
                mt={displayExplainations ? 20 : 10}
                aria-hidden="true"
            />
            <SimpleGrid cols={2} mt="0.5rem">
                <Box key={"Orginal"} sx={{ borderBottomColor: "#0BB779" }} className={classes.stat}>
                    <Stack position="apart" align="flex-start" spacing={0}>
                        <Text fz={smallVersion ? "sm" : "md"} fw={600}>
                            {t("scan.result.human")}
                        </Text>
                        <Text c={"var(--green-dark)"} fw={700} fz={smallVersion ? "xs" : "sm"}>
                            {Math.ceil((100 - calculatedAIScore) * 10) / 10}%
                        </Text>
                    </Stack>
                </Box>

                <Box key={"Kunstig intelligens"} sx={{ borderBottomColor: "#E22024" }} className={classes.stat}>
                    <Stack position="apart" align="flex-end" spacing={0}>
                        <Text fz={smallVersion ? "sm" : "md"} fw={600}>
                            {t("scan.result.ai")}
                        </Text>
                        <Text c={"var(--red)"} fw={700} fz={smallVersion ? "xs" : "sm"}>
                            {Math.floor(calculatedAIScore * 10) / 10}%
                        </Text>
                    </Stack>
                </Box>
            </SimpleGrid>

            {displayBlocks && blocks?.length > 0 && (calculatedAiScoreCategoryName === "ProbablyAiGenerated" || calculatedAiScoreCategoryName === "AiGenerated") &&
                <div className={style.blocksWrapper}>
                    <Text fz={14} color="var(--text-off)" fw={600}>{t("scan.result.suspicious-sentences")}</Text>

                    <div className={style.blocksScrollWrapper}>
                        <div className={style.blocksScrollArea} ref={blocksScrollRef} onScroll={updateBlocksOverflow}>
                            {blocks.map((block, index) => {

                                const { aiScore: blockAIScore, category: blockCategoryName } = calculateAndCategorizeAIScore(block.result.fake, scan?.version, scan?.aiDetectionStrictMode);

                                const blockScore = Math.min(99.5, Math.max(0.5, blockAIScore));

                                if (blockScore > 60) return (
                                    <div className={style.block} key={block.id}>
                                        <Text color={"dimmed"} fz={14}>{index + 1}.</Text>

                                        <span className={style.blockSetence} >{block.text}</span>

                                        <Text
                                            fw={600}
                                            fz={12}
                                            ta={"right"}
                                            color={blockCategoryName === "AiGenerated" ? "var(--red)" : blockCategoryName === "ProbablyAiGenerated" ? "var(--red)" : blockCategoryName === "Unsure" ? "var(--yellow)" : "var(--green-dark)"}
                                            radius={"xs"}
                                        >
                                            {`${blockScore.toFixed(1)}%`}
                                        </Text>

                                    </div>
                                )

                                return null;
                            })}
                        </div>

                        {blocksOverflow.top && <div className={`${style.blocksFade} ${style.blocksFadeTop}`} aria-hidden="true" />}
                        {blocksOverflow.bottom && <div className={`${style.blocksFade} ${style.blocksFadeBottom}`} aria-hidden="true" />}
                    </div>
                </div>
            }
        </div>
    );
};

export default InfoSlab;
